import FacebookIcon from "../../static/icons/FacebookIcon.svg";
import TwitterIcon from "../../static/icons/TwitterIcon.svg";
import ForumIcon from "../../static/icons/ChatIcon2.svg";
import RedditIcon from "../../static/icons/RedditIcon.svg";
import CraiglistIcon from "../../static/icons/CraiglistIcon.svg";

// Shown on the Appointments page and Lead Center → Opportunity view when
// the user lands via a dashboard drill-in. Explains why the list count can
// be higher than the dashboard cell they clicked. Kept in one place so the
// two surfaces stay verbatim consistent.
export const DASHBOARD_DRILL_IN_NOTE =
  "Showing all matching records, including those assigned to deactivated users. The dashboard excludes deactivated users, so this total may be higher.";

// Activity Center / browser-extension lead-search "scope" dropdown. Drives the
// `skipFilter` flag on the getLeads query: "all" → skipFilter true (search
// ignores active filters), "filtered" → skipFilter false (search within the
// applied-filter result set). Shared so both surfaces stay verbatim consistent.
export const SEARCH_SCOPE = {
  ALL: "all",
  FILTERED: "filtered",
};
export const SEARCH_SCOPE_OPTIONS = [
  {
    value: SEARCH_SCOPE.FILTERED,
    label: "Within Filtered Leads",
    description: "Search only the leads in your active filter",
  },
  {
    value: SEARCH_SCOPE.ALL,
    label: "All Leads",
    description: "Search all leads, regardless of filters",
  },
];

export const Colors = {
  lightBlue: "#CDEBF9",
  blue: "#079BE2",
  lightBrandBlue: "#E6F0FF",
  primaryBrandBlue: "#006593",
  lightPrimaryBrandBlue: "#007dae",
  darkPrimaryBrandBlue: "#005278",
  lightPurple: "#E4B6D4",
  brandPurple: "#A70E72",
  darkBrandPurple: "#8E0B61",
  lightGreen: "#E2F9F0",
  green: "#3DD57F",
  lightOrange: "#FFEDC6",
  orange: "#FFBE42",
  darkOrange: "#d29e35",
  lightRed: "#FFE7E7",
  red: "#FC5A5A",
  darkRed: "#d25454",
  white: "#FFFFFF",
  backgroundGray: "#F5F6FA",
  veryLightGray: "#EEEEF1",
  lightGray: "#EEEEF1",
  mediumGray: "#BDC4CB",
  gray: "#C4C1C2",
  medDarkgray: "#999697",
  darkGray: "#5E5E5E",
  disabledGray: "#D2D3D8",
};

export const ChartColors = [
  Colors.green,
  Colors.orange,
  Colors.brandPurple,
  Colors.primaryBrandBlue,
  Colors.red,
  Colors.blue,
];

export const LightColorPairs = [
  {
    light: Colors.lightBrandBlue,
    standard: Colors.primaryBrandBlue,
  },
  {
    light: Colors.lightOrange,
    standard: Colors.orange,
  },
  {
    light: Colors.lightPurple,
    standard: Colors.brandPurple,
  },
  {
    light: Colors.lightGreen,
    standard: Colors.green,
  },
  {
    light: Colors.lightRed,
    standard: Colors.red,
  },
  {
    light: Colors.lightBlue,
    standard: Colors.blue,
  },
];

export const TagColors = tag => {
  switch (tag) {
    case "Buy Car":
      return ChartColors[0];
    case "Sell Car":
      return ChartColors[1];
    case "New":
      return ChartColors[2];
    case "Used":
      return ChartColors[3];
    case "Parts":
      return ChartColors[4];
    case "Service":
      return ChartColors[5];
    default:
      return null;
  }
};

// TODO: Corner roundness?
export const Outlines = {
  thick: "4px",
  regular: "2px",
  thin: "1px",
};

// TODO: Cards

// TODO: Separators
export const Separators = (weight, color) => {
  const weights = {
    thin: "1px",
    medium: "2px",
    thick: "3px",
  };
  return `${weights[weight]} solid ${Colors[color]}`;
};

export const ProspectStatus = {
  REJECTED: "Rejected",
  SAVED: "Saved",
  ACCEPTED: "Accepted",
  VIEWED: "Viewed",
};

// Add more if necessary
export const SourceFilters = ["Others", "Forums", "Craiglist", "Quora", "Yelp", "Facebook"];
export const SourceTypes = {
  FORUM: ForumIcon,
  CRAIGSLIST: CraiglistIcon,
  FACEBOOK: FacebookIcon,
  TWITTER: TwitterIcon,
  REDDIT: RedditIcon,
};

export const DealStatus = {
  ACTIVE: "Active",
  DEAL_WON: "Deal Won",
  DEAL_LOST: "Deal Lost",
  NO_RESPONSE: "No response",
  PUSHED_CRM: "Pushed to CRM",
  EXPIRED: "Expired",
  ARCHIVED: "Archive",
};

export const DealStatusFilters = ["Active", "Deal Won", "Deal Lost"];

export const RangeFilters = [
  { value: 1, name: "Today" },
  { value: 3, name: "3 Days" },
  { value: 7, name: "Week" },
  { value: 14, name: "2 weeks" },
  { value: null, name: "All Posts" },
];

export const DealStatusSelects = [
  { value: "ACTIVE", display: "Active" },
  { value: "DEAL_WON", display: "Deal Won" },
  { value: "DEAL_LOST", display: "Deal Lost" },
  { value: "ARCHIVED", display: "Archive" },
];

// TODO: complete with correct industries
export const Industries = ["Industry 1", "Industry 2", "Industry 3"];

export const BAR = "BAR";
export const LINE = "LINE";
export const TREE = "TREE";
export const graphTypes = { BAR, LINE, TREE };

export const timezones = [
  { value: "America/Juneau", label: "Alaska Daylight Time", shortName: "AST" },
  {
    value: "America/Chicago",
    label: "Central Daylight Time (North America)",
    shortName: "CST",
  },
  {
    value: "America/New_York",
    label: "Eastern Daylight Time (North America)",
    shortName: "EST",
  },
  {
    value: "Pacific/Honolulu",
    label: "Hawaii-Aleutian Daylight Time",
    shortName: "HAST",
  },
  {
    value: "America/Denver",
    label: "Mountain Daylight Time (North America)",
    shortName: "MST",
  },
  {
    value: "America/Los_Angeles",
    label: "Pacific Daylight Time (North America)",
    shortName: "PST",
  },
  { value: "Asia/Calcutta", label: "Asia/Calcutta", shortName: "IST" },
];

export const intervals = [
  { name: "30 min", value: "30" },
  { name: "1 Hour", value: "60" },
  { name: "2 Hours", value: "120" },
  { name: "4 Hours", value: "240" },
  { name: "8 Hours", value: "480" },
  { name: "24 Hours", value: "1440" },
];

export const FilterSetTypes = {
  ANALYTICS: "ANALYTICS",
  PROSPECTS: "PROSPECTS",
  EXPORT: "EXPORT",
  LIFE_EVENTS: "LIFE_EVENTS",
  GENERAL: "GENERAL",
  ENGAGEMENTS: "ENGAGEMENTS",
};

export const NOT_ENOUGH_DATA =
  "Not enough data to display.\n Let's work on some leads and check back later.";

// filters
export const KEYWORD = "Keyword";
export const LOCATION = "Location";
export const SOURCES = "Sources";
export const MAKE = "Make";
export const MULTISELECT = "Multiselect";
export const SELECT = "Select";
export const ALL = "All";

// personalized ads
export const COUNTS = [100, 200, 300, 400];
export const FREQUENCY = [
  ["1 Day", 1],
  ["1 Week", 7],
  ["1 Month", 30],
];

export const TypeFiltersSelects = [
  { value: "ALL", display: "All" },
  { value: "PROSPECTS", display: "Prospects" },
  { value: "LIFE_EVENT", display: "Life Events" },
];

export const LEAD_TYPE_OPTION = [
  {
    key: "",
    value: "",
    label: "Select the type of lead",
    disabled: true,
  },
  {
    key: "sales",
    value: "sales",
    label: "Sales",
    disabled: false,
  },
  {
    key: "parts",
    value: "parts",
    label: "Parts",
    disabled: false,
  },
  {
    key: "service",
    value: "service",
    label: "Service",
    disabled: false,
  },
];

export const CONDITION_OPTION = [
  {
    key: "",
    value: "",
    label: "Select the condition",
    disabled: true,
  },
  {
    key: "new",
    value: "new",
    label: "New",
    disabled: false,
  },
  {
    key: "used",
    value: "used",
    label: "Used",
    disabled: false,
  },
];

export const INTEREST_OPTION = [
  {
    key: "",
    value: "",
    label: "Select the interest",
    disabled: true,
  },
  {
    key: "buy",
    value: "buy",
    label: "Buy",
    disabled: false,
  },
  {
    key: "sell",
    value: "sell",
    label: "Sell",
    disabled: false,
  },
  {
    key: "trade",
    value: "trade",
    label: "Trade",
    disabled: false,
  },
  {
    key: "service",
    value: "service",
    label: "Service",
    disabled: false,
  },
];
export const ordinalNumbers = [
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
  "Eleventh",
  "Twelfth",
  "Thirteenth",
  "Fourteenth",
  "Fifteenth",
  "Sixteenth",
  "Seventeenth",
  "Eighteenth",
  "Nineteenth",
  "Twentieth",
  "Twenty-First",
  "Twenty-Second",
  "Twenty-Third",
  "Twenty-Fourth",
  "Twenty-Fifth",
];
export const LABELS = [
  {
    label: "{firstName}",
    addKeyword: "{firstName}",
  },
  {
    label: "{lastName}",
    addKeyword: "{lastName}",
  },
  {
    label: "{sourceName}",
    addKeyword: "{sourceName}",
  },
  {
    label: "{companyName}",
    addKeyword: "{companyName}",
  },
  {
    label: "{vehicleOfInterest}",
    addKeyword: "{vehicleOfInterest}",
  },
  {
    label: "{tradeVehicle}",
    addKeyword: "{tradeVehicle}",
  },
  {
    label: "{locationName}",
    addKeyword: "{locationName}",
  },
  {
    label: "{createAppointmentUrl}",
    addKeyword: "{createAppointmentUrl}",
  },
];
export const AccessPermissionAction = {
  ACCESS_LOCATION_MANAGEMENT: "ACCESS_LOCATION_MANAGEMENT",
  LOCATION_VIEW: "LOCATION_MANAGEMENT_VIEW",
  LOCATION_ADD_EDIT: "LOCATION_MANAGEMENT_ADD_EDIT",
  LOCATION_DELETE: "LOCATION_MANAGEMENT_DELETE",
  LOCATION_MULTIPPLE_ACCESS: "ACCESS_LOCATION_MANAGEMENT_MULTIPPLE_ACCESS",
  TEMPLATE_VIEW: "TEMPLATE_MANAGEMENT_VIEW",
  TEMPLATE_ADD_EDIT: "TEMPLATE_MANAGEMENT_ADD_EDIT",
  TEMPLATE_DELETE: "TEMPLATE_MANAGEMENT_DELETE",
  TEMPLATE_TAB_VIEW: "TEMPLATE_MANAGEMENT_TAB_VIEW",
  CRM_INTEGRATION_VIEW: "CRM_INTEGRATION_MANAGEMENT_VIEW",
  CRM_INTEGRATION_ADD_EDIT: "CRM_INTEGRATION_MANAGEMENT_ADD_EDIT",
  CRM_INTEGRATION_DELETE: "CRM_INTEGRATION_MANAGEMENT_DELETE",
  APPOINTMENT_VIEW: "APPOINTMENT_MANAGEMENT_VIEW",
  APPOINTMENT_UPDATE: "APPOINTMENT_MANAGEMENT_UPDATE",
  APPOINTMENT_CANCEL: "APPOINTMENT_MANAGEMENT_CANCEL",
  APPOINTMENT_SCHEDULE: "APPOINTMENT_MANAGEMENT_SCHEDULE",
  APPOINTMENT_TAB_VIEW: "APPOINTMENT_MANAGEMENT_TAB_VIEW",
  CAMPAIGN_VIEW: "CAMPAIGN_MANAGEMENT_VIEW",
  CAMPAIGN_CREATE: "CAMPAIGN_MANAGEMENT_CREATE",
  CAMPAIGN_DELETE: "CAMPAIGN_MANAGEMENT_DELETE",
  CAMPAIGN_TAB_VIEW: "CAMPAIGN_MANAGEMENT_TAB_VIEW",
  CAMPAIGN_MANAGEMENT_UPDATE: "CAMPAIGN_MANAGEMENT_UPDATE",
  REVIEW_SETTING_VIEW: "REVIEW_SETTING_MANAGEMENT_VIEW",
  REVIEW_SETTING_ADD_EDIT: "REVIEW_SETTING_MANAGEMENT_ADD_EDIT",
  REVIEW_SETTING_DELETE: "REVIEW_SETTING_MANAGEMENT_DELETE",
  PHONE_BOT_VIEW: "PHONE_BOT_MANAGEMENT_VIEW",
  PHONE_BOT_ADD_EDIT: "PHONE_BOT_MANAGEMENT_ADD_EDIT",
  PHONE_BOT_DELETE: "PHONE_BOT_MANAGEMENT_DELETE",
  WORKING_HOUR_VIEW: "WORKING_HOUR_MANAGEMENT_VIEW",
  WORKING_HOUR_UPDATE: "WORKING_HOUR_MANAGEMENT_UPDATE",
  LEAD_VIEW: "LEAD_MANAGEMENT_VIEW",
  LEAD_ADD_EDIT: "LEAD_MANAGEMENT_ADD_EDIT",
  LEAD_DELETE: "LEAD_MANAGEMENT_DELETE",
  LEAD_OWNER_VIEW: "LEAD_OWNER_MANAGEMENT_VIEW",
  LEAD_OWNER_ADD_EDIT: "LEAD_OWNER_MANAGEMENT_ADD_EDIT",
  LEAD_OWNER_DELETE: "LEAD_OWNER_MANAGEMENT_DELETE",
  LEAD_VEHICLE_VIEW: "LEAD_VEHICLE_MANAGEMENT_VIEW",
  LEAD_VEHICLE_ADD_EDIT: "LEAD_VEHICLE_MANAGEMENT_ADD_EDIT",
  LEAD_VEHICLE_DELETE: "LEAD_VEHICLE_MANAGEMENT_DELETE",
  LEAD_SEND_TO_STORE: "LEAD_SEND_TO_STORE",
  LEAD_MANAGEMENT_DISABLED_CONVERSATION: "LEAD_MANAGEMENT_DISABLED_CONVERSATION",
  LEAD_MANAGEMENT_USER_REASSIGNMENT: "LEAD_MANAGEMENT_USER_REASSIGNMENT",
  USER_VIEW: "USER_MANAGEMENT_VIEW",
  USER_ADD_EDIT: "USER_MANAGEMENT_ADD_EDIT",
  USER_DELETE: "USER_MANAGEMENT_DELETE",
  USER_PERMISSION_MODIFY: "USER_MANAGEMENT_PERMISSION_MODIFY",
  LEAD_CENTER_TAB_VIEW: "LEAD_CENTER_TAB_VIEW",
  ACTIVITY_CENTER_TAB_VIEW: "ACTIVITY_CENTER_TAB_VIEW",
  ACTIVITY_CENTER_RESTRICT_REMOVE_DEFAULT_FILTER: "ACTIVITY_CENTER_RESTRICT_REMOVE_DEFAULT_FILTER",
  // Reply-As (EAI-1530): gates the whole feature. When true the Reply-As
  // selector is shown, the saved reply-as preference is applied by default, and
  // sends are attributed to the selected signer. When false, sends go as the
  // logged-in user only (no selector, no preference).
  ACTIVITY_CENTER_REPLY_AS_ENABLE: "ACTIVITY_CENTER_REPLY_AS_ENABLE",
  SCHEDULE_TAB_VIEW: "SCHEDULE_TAB_VIEW",
  DASHBOARD_TAB_VIEW: "DASHBOARD_TAB_VIEW",
  LEAD_MANAGEMENT_PUSH_TO_CRM: "LEAD_MANAGEMENT_PUSH_TO_CRM",
  LEAD_MANAGEMENT_SEND_TO_STORE: "LEAD_MANAGEMENT_SEND_TO_STORE",
  LEAD_MANAGEMENT_ALLOW_USER_MODIFY_OTTO_RESPONDER_STATUS:
    "LEAD_MANAGEMENT_ALLOW_USER_MODIFY_OTTO_RESPONDER_STATUS",
  // Email consent (EAI-1666): gates changing a lead's email consent. Viewing the
  // status only needs LEAD_MANAGEMENT_VIEW, which already gates the lead profile.
  // The backend enforces this independently in the modifyEmailConsent mutation.
  LEAD_MANAGEMENT_ALLOW_USER_MODIFY_EMAIL_CONSENT:
    "LEAD_MANAGEMENT_ALLOW_USER_MODIFY_EMAIL_CONSENT",
  USER_PROFILE_USER_INFORMATION: "USER_PROFILE_USER_INFORMATION",
  USER_PROFILE_UPDATE_CRM_INFORMATION: "USER_PROFILE_UPDATE_CRM_INFORMATION",
  USER_PROFILE_USER_PASSWORD: "USER_PROFILE_USER_PASSWORD",
  TEAM_MANAGEMENT_CREATE: "TEAM_MANAGEMENT_CREATE",
  TEAM_MANAGEMENT_UPDATE: "TEAM_MANAGEMENT_UPDATE",
  TEAM_MANAGEMENT_VIEW: "TEAM_MANAGEMENT_VIEW",
  REGION_MANAGEMENT_CREATE: "REGION_MANAGEMENT_CREATE",
  REGION_MANAGEMENT_UPDATE: "REGION_MANAGEMENT_UPDATE",
  REGION_MANAGEMENT_VIEW: "REGION_MANAGEMENT_VIEW",
  REVIEW_MANAGEMENT_VIEW: "REVIEW_MANAGEMENT_VIEW",
  REVIEW_MANAGEMENT_RESPOND: "REVIEW_MANAGEMENT_RESPOND",
  REVIEW_MANAGEMENT_REQUEST_AND_TRACK: "REVIEW_MANAGEMENT_REQUEST_AND_TRACK",
  PROMPT_MANAGEMENT_VIEW: "PROMPT_MANAGEMENT_VIEW",
  PROMPT_MANAGEMENT_CREATE: "PROMPT_MANAGEMENT_CREATE",
  PROMPT_MANAGEMENT_ADD_EDIT: "PROMPT_MANAGEMENT_ADD_EDIT",
  PROMPT_MANAGEMENT_UPDATE: "PROMPT_MANAGEMENT_UPDATE",
  PROMPT_MANAGEMENT_DELETE: "PROMPT_MANAGEMENT_DELETE",
  LEAD_OPPORTUNITY_MANAGEMENT_VIEW: "LEAD_OPPORTUNITY_MANAGEMENT_VIEW",
  LEAD_OPPORTUNITY_MANAGEMENT_ADD_EDIT: "LEAD_OPPORTUNITY_MANAGEMENT_ADD_EDIT",
  LEAD_OPPORTUNITY_MANAGEMENT_DELETE: "LEAD_OPPORTUNITY_MANAGEMENT_DELETE",
  USER_CALENDAR_MANAGEMENT: "USER_CALENDAR_MANAGEMENT",
  TRANSLATION_MANAGEMENT_VIEW: "TRANSLATION_MANAGEMENT_VIEW",
  VDP_FEATURE_VIEW: "VDP_FEATURE_VIEW",
  WIDGET_MANAGEMENT_VIEW: "WIDGET_MANAGEMENT_VIEW",
  WIDGET_MANAGEMENT_ADD_EDIT: "WIDGET_MANAGEMENT_ADD_EDIT",
  WIDGET_MANAGEMENT_DELETE: "WIDGET_MANAGEMENT_DELETE",
  MERGE_LEADS_OPPS: "MERGE_LEADS_OPPS",
  EMAIL_INBOX_VIEW: "EMAIL_INBOX_VIEW", // Enable/Disable Email Tab
  EMAIL_MANAGEMENT_VIEW: "EMAIL_MANAGEMENT_VIEW", // Email view permission at Activity Center timeline
  EMAIL_MANAGEMENT_DELETE: "EMAIL_MANAGEMENT_DELETE", // Email delete/trash permission at Email Tab
  EMAIL_MANAGEMENT_SEND: "EMAIL_MANAGEMENT_SEND", // Disable Send Email tab and button at Activity Center
  TASK_MANAGEMENT_TAB_VIEW: "TASK_MANAGEMENT_TAB_VIEW",
  TASK_MANAGEMENT_VIEW: "TASK_MANAGEMENT_VIEW",
  TASK_MANAGEMENT_ADD_EDIT: "TASK_MANAGEMENT_ADD_EDIT",
  TASK_MANAGEMENT_DELETE: "TASK_MANAGEMENT_DELETE",
  TASK_MANAGEMENT_LOCATION_ACCESS: "TASK_MANAGEMENT_LOCATION_ACCESS",
  TASK_MANAGEMENT_TEAM_ACCESS: "TASK_MANAGEMENT_TEAM_ACCESS",
  CALL_MANAGEMENT_AUDIO: "CALL_MANAGEMENT_AUDIO",
  CALL_MANAGEMENT_VIDEO: "CALL_MANAGEMENT_VIDEO",
  REPORT_MANAGEMENT_VIEW_EMAIL_REPORT: "REPORT_MANAGEMENT_VIEW_EMAIL_REPORT",
  REPORT_MANAGEMENT_VIEW_OTTO_REPORT: "REPORT_MANAGEMENT_VIEW_OTTO_REPORT",
  // OttoVin (vehicle-discovery) — basic feature visibility. Backend permission
  // rows not yet seeded; until then only platform admins (bypass) see gated UI.
  OTTOVIN_VIEW_BASIC: "OTTOVIN_MANAGEMENT_VIEW_BASIC",
  OTTOVIN_VIEW: "OTTOVIN_MANAGEMENT_VIEW",
  // OttoVin — send lead-attributed vehicle links from the Activity Center.
  OTTOVIN_SEND: "OTTOVIN_MANAGEMENT_SEND",
  // OttoVin — view the vehicle/content-engagement Reports page (Reports ▸ OttoVin).
  // Backend rows not yet seeded; until then only platform admins (bypass) see it.
  OTTOVIN_VIEW_OTTO_REPORT: "OTTOVIN_MANAGEMENT_VIEW_OTTO_REPORT",
  // OttoVin — generate/manage QR-print PDF jobs (Inventory ▸ Print QR). Backend
  // rows not yet seeded; until then only platform admins (bypass) can use it.
  OTTOVIN_ADD_EDIT: "OTTOVIN_MANAGEMENT_ADD_EDIT",

  // Vehicle Assets → Documents (Inventory tab). VIEW lists documents; MANAGE
  // uploads / edits / deletes. Backend rows not yet seeded for non-admins; until
  // then only platform admins (bypass) see the manage controls.
  DOCUMENTS_VIEW: "DOCUMENTS_MANAGEMENT_VIEW_DOCUMENT",
  DOCUMENTS_MANAGE: "DOCUMENTS_MANAGEMENT_MANAGE_DOCUMENT",
  // Finance-related documents (see FINANCE_DOC_TYPES in documents/mockDocuments).
  // Without this, those documents are filtered out of the Vehicle Assets lists and
  // the type is not offered on upload. Seeded in the backend.
  DOCUMENTS_VIEW_FINANCE: "DOCUMENTS_MANAGEMENT_VIEW_FINANCE_DOCUMENT",

  // Vehicle Inventory — per-tab visibility inside the Inventory page.
  // NOTE: the seeded action code is misspelled "WHISHLIST" (see
  // docs/Vechile_Inventory_Permission_script.sql); the value must match the DB
  // exactly, so do not "fix" the spelling here.
  VEHICLE_INVENTORY_TAB_VIEW: "VEHICLE_INVENTORY_MANAGEMENT_INVENTORY_TAB_VIEW",
  VEHICLE_INVENTORY_WISHLIST_TAB_VIEW: "VEHICLE_INVENTORY_MANAGEMENT_WHISHLIST_TAB_VIEW",

  REPORT_MANAGEMENT_VIEW_CALL_REPORT: "REPORT_MANAGEMENT_VIEW_CALL_REPORT",
  REPORT_MANAGEMENT_INDIVIDUAL_VIEW: "REPORT_MANAGEMENT_INDIVIDUAL_VIEW",
  REPORT_MANAGEMENT_TEAM_WISE_VIEW: "REPORT_MANAGEMENT_TEAM_WISE_VIEW",
  REPORT_MANAGEMENT_LOCATION_WISE_VIEW: "REPORT_MANAGEMENT_LOCATION_WISE_VIEW",
  REPORT_MANAGEMENT_COMPANY_WISE_VIEW: "REPORT_MANAGEMENT_COMPANY_WISE_VIEW",
  REPORT_MANAGEMENT_DELETE: "REPORT_MANAGEMENT_DELETE",
  REPORT_MANAGEMENT_VIEW_SCHEDULED_REPORT: "REPORT_MANAGEMENT_VIEW_SCHEDULED_REPORT",
  REPORT_MANAGEMENT_SCHEDULE: "REPORT_MANAGEMENT_SCHEDULE",
  REPORT_MANAGEMENT_VIEW: "REPORT_MANAGEMENT_VIEW",
  AUTORESPONDER_FEEDBACK_TAB_VIEW: "AUTORESPONDER_FEEDBACK_TAB_VIEW",
  AUTORESPONDER_FEEDBACK_SUBMIT: "AUTORESPONDER_FEEDBACK_SUBMIT",
  REPORT_MANAGEMENT_VIEW_CHECKOUT_REPORT: "REPORT_MANAGEMENT_VIEW_CHECKOUT_REPORT",
};

export const currencies = [
  { name: "United States Dollar", symbol: "$", code: "USD" },
  { name: "Euro", symbol: "€", code: "EUR" },
  { name: "Japanese Yen", symbol: "¥", code: "JPY" },
  { name: "British Pound Sterling", symbol: "£", code: "GBP" },
  { name: "Australian Dollar", symbol: "A$", code: "AUD" },
  { name: "Canadian Dollar", symbol: "CA$", code: "CAD" },
  { name: "Swiss Franc", symbol: "CHF", code: "CHF" },
  { name: "Chinese Yuan", symbol: "¥", code: "CNY" },
  { name: "Indian Rupee", symbol: "₹", code: "INR" },
  { name: "Brazilian Real", symbol: "R$", code: "BRL" },
  // Add more currencies as needed
];

export const EVENTS = "Events";

export const multiSelectFilterSelector = typeName => item =>
  item.typeName === typeName && item.type === MULTISELECT;
export const MODAL_CONTENT = "When clicked the OK button, it cannot be recovered";
export const MODAL_TITLE = "Do you want to continue?";

export const leadStats = [
  {
    label: "Total Leads",
    key: "totalLeads",
    color: "#DBEAFE",
  },
  {
    label: "Responded",
    key: "totalResponded",
    color: "#DCFCE7",
  },
  {
    label: "Reached",
    key: "totalDelivered",
    color: "#FEF3C7",
  },
  {
    label: "Engagements",
    key: "totalEngaged",
    color: "#FEE2E2",
  },
];

export const appointmentStats = [
  {
    label: "Scheduled",
    key: "scheduledAppointmentCount",
    color: "#DBEAFE",
  },
  {
    label: "Showed",
    key: "showedAppointmentCount",
    color: "#DCFCE7",
  },
  {
    label: "No-Showed",
    key: "noShowedAppointmentCount",
    color: "#FEF3C7",
  },
  {
    label: "Cancelled",
    key: "cancelledAppointmentCount",
    color: "#FEE2E2",
  },
];

export const SNIPPET_OPTIONS = [
  "{leadFullName}",
  "{leadFirstName}",
  "{leadLastName}",
  "{leadOwner}",
  "{appointmentSummary}",
  "{companyName}",
  "{appointmentDateTime}",
  "{locationUrl}",
  "{locationName}",
  "{locationAddress}",
  "{userName}",
  "{firstName}",
  "{lastName}",
  "{sourceName}",
  "{userName}",
  "{companyName}",
  "{locationName}",
  "{previousVehicle}",
  "{createAppointmentUrl}",
  "{vehicleOfInterest}",
  "{locationAddress}",
  "{locationUrl}",
  "{vehicleOfInterestDesc}",
  "{appointmentDate}",
  "{eventDate}",
];
export const MESSAGE_CONTENT_TYPES = {
  WELCOME_MESSAGE: "Welcome Message",
  // # LEAD
  NEW_MESSAGE_RECEIVED: "Received Message from Lead",
  NEW_MESSAGE_SENT: "Sent Message to Lead",
  CALLING_THE_LEAD: "Calling the lead",
  NEW_MESSAGE_REMINDER: "new lead message reminder",
  // # REVIEW
  REVIEW_BAD_FROM_LEAD: "Bad Review From Lead",
  REVIEW_REQUEST_SENT: "Review Request sent to lead",
  MANUAL_REVIEW_REQUEST_SENT_TO_LEAD: "Manual Review Request Sent To Lead",
  REVIEW_RECEIVED_FROM_LEAD: "Received a review from a lead",
  GOOD_REVIEW_GIVEN_FROM_LEAD: "Lead has given a good review.",
  BAD_REVIEW_FROM_LEAD: "Lead has given a bad review.",
  CUSTOMER_COPIED_REVIEW_SUGGESTION: "Customer Copied Review Suggestion",
  CUSTOMER_GENERATED_REVIEW_SUGGESTION: "Customer Generated Review Suggestion",
  GOOGLE_REVIEW_LINK_CLICKED: "Google Review Link Clicked",
  FACEBOOK_REVIEW_LINK_CLICKED: "Facebook Review Link Clicked",
  GOOD_REVIEW_FROM_LEAD: "Good Review From Lead",
  YELP_REVIEW_LINK_CLICKED: "Yelp Review Link Clicked",
  DEALER_RATER_REVIEW_LINK_CLICKED: "Dealer Rater Review Link Clicked",
  CARS_COM_LINK_CLICKED: "Cars.com Review Link Clicked",
  // # APPOINTMENT
  SENT_APPOINTMENT_RESCHEDULE_LINK_TO_LEAD: "Sent Appointment Reschedule Link To Lead",
  NOT_ACTIVE_APPOINTMENT: "Not Active Appointments",
  NOT_ACTIVE_APPOINTMENT_TO_LEAD: "No Active Appointment Notification To Lead",
  APPOINTMENT_RESCHEDULE: "Rescheduled Appointment",
  APPOINTMENT_SCHEDULED_NOTIFICATION_TO_USER: "Scheduled Appointment Notification To User",
  APPOINTMENT_RESCHEDULED_NOTIFICATION_TO_USER: "Rescheduled Appointment Notification To User",
  APPOINTMENT_SCHEDULED_TO_USER: "Scheduled Appointment To User",
  APPOINTMENT_RESCHEDULED_ALERT_TO_USER: "Appointment Reschedule Request Alert To User",
  APPOINTMENT_PAST_REMINDER: "Past Appointment Reminder",
  APPOINTMENT_PAST_REMINDER_TO_LEAD: "Past Appointment Reminder To Lead",
  APPOINTMENT_CANCELLED: "Appointment Cancelled",
  APPOINTMENT_REMINDER_MESSAGE_TO_LEAD: "Appointment Reminder Message to Lead",
  NEW_APPOINTMENT_CREATED: "Create New Appointment",
  APPOINTMENT_CANCELLED_TO_LEAD: "Appointment Cancelled Notification To Lead",
  APPOINTMENT_CANCELLED_TO_USER: "Appointment Cancelled Notification To User",
  APPOINTMENT_SCHEDULED_TO_LEAD: "Scheduled Appointment Notification To Lead",
  APPOINTMENT_RESCHEDULE_TO_USER: "Rescheduled Appointment Notification To User",
  APPOINTMENT_CONFIRMED_TO_LEAD: "Confirmed Appointment Notification To Lead",
  NEW_APPOINTMENT_CREATED_TO_LEAD: "New Appointment Notification To Lead",
  REQUEST_TO_RESCHEDULE_APPOINTMENT: "Request to Reschedule Appointment",
  APPOINTMENT_CONFIRMED: "Appointment Confirmed",
  APPOINTMENT_CONFIRMED_NOTIFICATION_TO_USER: "Appointment Confirmed Notification To User",
  APPOINTMENT_CONFIRMED_NOTIFICATION_TO_LEAD: "Appointment Confirmed Notification To Lead",
  APPOINTMENT_RESCHEDULE_TO_LEAD: "Rescheduled Appointment Notification To Lead",
  APPOINTMENT_SCHEDULED: "Scheduled Appointment",
  APPOINTMENT_NO_SHOWED_STATUS_TO_LEAD: "Appointment NO_SHOWED Notification To Lead",
  APPOINTMENT_SHOWED_STATUS_TO_LEAD: "Appointment SHOWED Notification To Lead",
  APPOINTMENT_STATUS_UPDATE_BY_OTTO: "Appointment Status Updated By SMAI Ottobot",
  APPOINTMENT_STATUS_UPDATE: "Update the Appointment Status",

  // # CAMPAIGN
  CAMPAIGN: "campaign",
  CAMPAIGN_MESSAGE_TO_LEAD: "Campaign Message to Lead",
  CAMPAIGN_REVIEW_REQUEST_SENT_TO_LEAD: "Campaign Review Request Sent To Lead",

  // # Schedule
  SCHEDULED_MESSAGE_SENT: "Sent Scheduled Message To Lead",
  SCHEDULED_MESSAGE: "Scheduled Message To Lead",
  SCHEDULED_MESSAGE_TO_LEAD: "Rescheduled Message To Lead",
  SCHEDULED_MESSAGE_CANCELLED: "Cancelled Scheduled Message To Lead",

  // # Nudges
  NUDGE_NEW_LEAD_MESSAGE: "new lead message",
  NUDGE_RECEIVED_BY_SMAI_OTTO_FROM_USER: "Message Received By SMAI Ottobot From User",

  // # OTTO MESSAGE
  AI_RESPONSE_TO_LEAD: "AI Response To Lead",

  // # Merged Records
  NEW_MESSAGE_MERGED_LEAD: "new lead message for merged lead",

  // # Opportunity Merged
  NEW_OPPORTUNITY_MESSAGE_MERGED_LEAD: "new opportunity message for merged opportunity",

  PRICE_ALERT_CAMPAIGN_LEAD: "Price Alert Message to Lead",
  FOLLOW_UP_LOST_LEAD_CAMPAIGN_LEAD: "Follow Up Lost Lead Message to Lead",
};

/**
 * `messageType` values that are not conversation.
 *
 * These ride in the same feed as real messages but record something that
 * happened TO the lead rather than something anyone said — a merge, or a
 * consent change. They render as system rows in the thread, and anywhere the
 * UI asks "what was the last message?" they must be skipped or the lead list
 * shows a raw audit payload as the preview. Mirrors the API's own
 * NON_CONVERSATION_MESSAGE_TYPES.
 */
export const MESSAGE_TYPES = {
  MERGE_LEAD: "MERGE_LEAD",
  MERGE_OPPORTUNITY: "MERGE_OPPORTUNITY",
  CONSENT_UPDATE: "CONSENT_UPDATE",
};

export const NON_CONVERSATION_MESSAGE_TYPES = [
  MESSAGE_TYPES.MERGE_LEAD,
  MESSAGE_TYPES.MERGE_OPPORTUNITY,
  MESSAGE_TYPES.CONSENT_UPDATE,
];

/**
 * False for merges and consent changes — see NON_CONVERSATION_MESSAGE_TYPES.
 *
 * Accepts both shapes the message arrives in: the camelCase GraphQL row and the
 * snake_case SocketIO notification payload. Without the snake_case read every
 * socket-delivered CONSENT_UPDATE passed as conversation, which is how an
 * opt-out's audit JSON ended up as a lead row's preview text (EAI-1820).
 */
export const isConversationMessage = msg =>
  !NON_CONVERSATION_MESSAGE_TYPES.includes(
    String(msg?.messageType ?? msg?.message_type ?? "").toUpperCase()
  );

// Content types that should be hidden from the message feed
export const HIDDEN_MESSAGE_CONTENT_TYPES = [
  // Review related content types
  MESSAGE_CONTENT_TYPES.GOOD_REVIEW_FROM_LEAD,
  MESSAGE_CONTENT_TYPES.REVIEW_BAD_FROM_LEAD,
  MESSAGE_CONTENT_TYPES.BAD_REVIEW_FROM_LEAD,
  MESSAGE_CONTENT_TYPES.GOOD_REVIEW_GIVEN_FROM_LEAD,
  MESSAGE_CONTENT_TYPES.CUSTOMER_GENERATED_REVIEW_SUGGESTION,
  MESSAGE_CONTENT_TYPES.FACEBOOK_REVIEW_LINK_CLICKED,
  MESSAGE_CONTENT_TYPES.GOOGLE_REVIEW_LINK_CLICKED,
  MESSAGE_CONTENT_TYPES.YELP_REVIEW_LINK_CLICKED,
  MESSAGE_CONTENT_TYPES.CUSTOMER_COPIED_REVIEW_SUGGESTION,
  MESSAGE_CONTENT_TYPES.CARS_COM_LINK_CLICKED,
  MESSAGE_CONTENT_TYPES.DEALER_RATER_REVIEW_LINK_CLICKED,
];

export const NOTIFICATION_TYPES = {
  NEW_MESSAGE_FROM_LEAD: "NEW_MESSAGE_FROM_LEAD",
  APPOINTMENT_SCHEDULED: "APPOINTMENT_SCHEDULED",
  APPOINTMENT_RESCHEDULED: "APPOINTMENT_RESCHEDULED",
  APPOINTMENT_CONFIRMED: "APPOINTMENT_CONFIRMED",
  APPOINTMENT_SHOWED: "APPOINTMENT_SHOWED",
  APPOINTMENT_NO_SHOWED: "APPOINTMENT_NO_SHOWED",
  APPOINTMENT_CANCELLED: "APPOINTMENT_CANCELLED",
  REMINDER_MESSAGE: "REMINDER_MESSAGE",
  REVIEW_REQUEST: "REVIEW_REQUEST",
  RESPONSE_FROM_PROSPECT: "RESPONSE_FROM_PROSPECT",
  PROSPECTS: "PROSPECTS",
  LIFE_EVENTS: "LIFE_EVENTS",
  OTHER: "OTHER",
  INTERNAL_MESSAGE_TO_USER: "INTERNAL_MESSAGE_TO_USER",
};

// Review Request Analysis tile → review-detail-report drill-in. Each metric
// maps to one `reviewStatus` value sent to `getUserReviewDetail`.
export const REVIEW_STATUS = {
  INVITE_SENT: "invites_sent",
  REVIEW_COMPLETED: "review_completed",
  INVITE_NOT_SENT: "invite_not_sent",
};

/**
 * Which surface a thumbs up/down was given on — sent as `feedbackType` on
 * createMessageFeedback so the BE can tell the three sources apart:
 *
 *  AUTORESPONDER — outbound message in the conversation thread (ToLeadSMS /
 *                  MessageMetaInfo thumbs)
 *  CAMPAIGN      — Otto message rendered in the center of the thread
 *  DRAFT         — AI-generated draft rated in the composer before sending,
 *                  keyed by aiResponseHistoryId instead of a messageId
 */
export const MESSAGE_FEEDBACK_TYPE = {
  AUTORESPONDER: "AUTORESPONDER",
  CAMPAIGN: "CAMPAIGN",
  DRAFT: "DRAFT",
};

export const COMMON_PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
  DEFAULT_CURRENT_PAGE: 1,
  PAGE_SIZE_OPTIONS: ["10", "20", "50", "100"],
  TABLE_POSITION: ["bottomLeft"],
};

export const TABLE_MISSING_DATA_PLACEHOLDER = "--";

export const ANTD_MODAL_OPEN_POSITION = true; // true - Centered, false - Not Centered
export const MESSAGE_CONTAINER_CLASSNAME = "chat-message-item";
export const EMPTY_PLACEHOLDER = "--";

// Company sorting constants
export const COMPANY_SORT_FIELDS = {
  NAME: "name",
  DATE_CREATED: "date_created",
  USER_COUNT: "user_count",
};

export const SORT_DIRECTIONS = {
  ASC: "asc",
  DESC: "desc",
};
// Message scroll/navigation constants
export const URL_PARAM_MESSAGE_ID = "message_id"; // URL parameter name for messageId
export const QUERY_VAR_TARGET_MESSAGE_ID = "internalMessageId"; // GraphQL query variable name for messageId

export const APPOINTMENT_OFFSET = {
  AFTER_OPEN: 30,
  BEFORE_CLOSE: 30,
};

export const TENTATIVE_TIME_VALUE = "Tentative Time";
export const TENTATIVE_FALLBACK_TIME = "00:01 AM";

// appointment.extraData.created_by_user_type value indicating the appointment
// was created by the lead (vs. a dealer user).
export const CREATED_BY_USER_TYPE_LEAD = "LEAD";

export const VEHICLE_INTERESTS_TYPE = {
  BUY: "BUY",
  SELL: "SELL",
  TRADE: "TRADE",
};
